import {
  createStylesParams,
  type IStyleParam,
  type ISiteTextPreset,
  StyleParamType,
  wixColorParam,
  wixFontParam,
} from '@wix/tpa-settings';
import { StyleType } from '@wix/editor-platform-ooi-metadata-resolver';

import { tpaColorName } from '../../../../../utils/color';

export type DesignStylesParams = {
  titleColor: StyleParamType.Color;
  titleFont: StyleParamType.Font;
  orderNumberColor: StyleParamType.Color;
  orderNumberFont: StyleParamType.Font;
  statusColor: StyleParamType.Color;
  statusFont: StyleParamType.Font;
  subtitleColor: StyleParamType.Color;
  subtitleFont: StyleParamType.Font;
  bodyColor: StyleParamType.Color;
  bodyFont: StyleParamType.Font;
  sectionLabelColor: StyleParamType.Color;
  sectionLabelFont: StyleParamType.Font;
  detailLabelsColor: StyleParamType.Color;
  detailLabelsFont: StyleParamType.Font;
  detailTextColor: StyleParamType.Color;
  detailTextFont: StyleParamType.Font;

  backgroundColor: StyleParamType.Color;
  backgroundBorderWidth: StyleParamType.Number;
  backgroundBorderColor: StyleParamType.Color;
  backgroundCornerRadiusTopLeft: StyleParamType.Number;
  backgroundCornerRadiusTopRight: StyleParamType.Number;
  backgroundCornerRadiusBottomLeft: StyleParamType.Number;
  backgroundCornerRadiusBottomRight: StyleParamType.Number;
  backgroundCornerRadiusLinked: StyleParamType.Number;

  fulfillmentBackgroundColor: StyleParamType.Color;
  fulfillmentBorderWidth: StyleParamType.Number;
  fulfillmentBorderColor: StyleParamType.Color;
  fulfillmentCornerRadiusTopLeft: StyleParamType.Number;
  fulfillmentCornerRadiusTopRight: StyleParamType.Number;
  fulfillmentCornerRadiusBottomLeft: StyleParamType.Number;
  fulfillmentCornerRadiusBottomRight: StyleParamType.Number;
  fulfillmentCornerRadiusLinked: StyleParamType.Number;
  dividerWidth: StyleParamType.Number;
  dividerColor: StyleParamType.Color;

  appBackgroundColor: StyleParamType.Color;
};

type HtmlTag = 'h1' | 'h2' | 'h3' | 'p';

const fontParam = (
  preset: keyof ISiteTextPreset,
  htmlTag: HtmlTag,
  size: number,
  description: string,
): Omit<IStyleParam<StyleParamType.Font>, 'name'> => ({
  type: StyleParamType.Font,
  getDefaultValue: wixFontParam(preset, { htmlTag, size }),
  metadata: {
    type: StyleType.font,
    defaultValue: { type: 'static' as const, preset, size },
    ai: { description },
  },
});

const colorParam = (
  color: string,
  preset: string,
  description: string,
  type:
    | StyleType.color
    | StyleType.backgroundColor
    | StyleType.borderColor = StyleType.color,
  opacity = 1,
): Omit<IStyleParam<StyleParamType.Color>, 'name'> => ({
  type: StyleParamType.Color,
  getDefaultValue: wixColorParam(tpaColorName(color), opacity),
  metadata: {
    type,
    defaultValue: { type: 'static' as const, preset, opacity },
    ai: { description },
  },
});

const numberParam = (
  type:
    | StyleType.borderWidth
    | StyleType.borderTopLeftRadius
    | StyleType.borderTopRightRadius
    | StyleType.borderBottomLeftRadius
    | StyleType.borderBottomRightRadius
    | StyleType.numberEnum,
  value: number,
  description: string,
): Omit<IStyleParam<StyleParamType.Number>, 'name'> => ({
  type: StyleParamType.Number,
  getDefaultValue: () => value,
  metadata: {
    type,
    defaultValue: { type: 'static' as const, value },
    ai: { description },
  },
});

const legacyCardStylesParams = createStylesParams<
  Pick<
    DesignStylesParams,
    | 'backgroundColor'
    | 'backgroundBorderWidth'
    | 'backgroundBorderColor'
    | 'backgroundCornerRadiusTopLeft'
    | 'backgroundCornerRadiusTopRight'
    | 'backgroundCornerRadiusBottomLeft'
    | 'backgroundCornerRadiusBottomRight'
    | 'backgroundCornerRadiusLinked'
  >
>({
  backgroundColor: colorParam(
    'wst-primary-background-color',
    'color-fill-background-primary',
    'Background fill color of the order status card.',
    StyleType.backgroundColor,
  ),
  backgroundBorderWidth: numberParam(
    StyleType.borderWidth,
    0,
    'Border thickness in pixels of the order status card.',
  ),
  backgroundBorderColor: colorParam(
    'wst-paragraph-2-color',
    'color-text-primary',
    'Border color of the order status card.',
    StyleType.borderColor,
    0.2,
  ),
  backgroundCornerRadiusTopLeft: numberParam(
    StyleType.borderTopLeftRadius,
    0,
    'Top-left corner radius in pixels of the order status card.',
  ),
  backgroundCornerRadiusTopRight: numberParam(
    StyleType.borderTopRightRadius,
    0,
    'Top-right corner radius in pixels of the order status card.',
  ),
  backgroundCornerRadiusBottomLeft: numberParam(
    StyleType.borderBottomLeftRadius,
    0,
    'Bottom-left corner radius in pixels of the order status card.',
  ),
  backgroundCornerRadiusBottomRight: numberParam(
    StyleType.borderBottomRightRadius,
    0,
    'Bottom-right corner radius in pixels of the order status card.',
  ),
  backgroundCornerRadiusLinked: numberParam(
    StyleType.numberEnum,
    0,
    'Controls whether the four corner radii of the order status card are edited together: 1 = linked, 0 = independent.',
  ),
});

export const designStylesParams = createStylesParams<DesignStylesParams>({
  titleColor: colorParam(
    'wst-heading-2-color',
    'color-title',
    'Text color of the page title on the order tracking page.',
  ),
  titleFont: fontParam(
    'Heading-M',
    'h1',
    28,
    'Font of the page title on the order tracking page.',
  ),
  orderNumberColor: colorParam(
    'wst-paragraph-2-color',
    'color-text-primary',
    'Text color of the order number label and value on the order tracking page.',
  ),
  orderNumberFont: fontParam(
    'Body-M',
    'p',
    16,
    'Font of the order number label and value on the order tracking page.',
  ),
  statusColor: colorParam(
    'wst-paragraph-2-color',
    'color-text-primary',
    'Text color of the status label on the order tracking page.',
  ),
  statusFont: fontParam(
    'Body-M',
    'p',
    16,
    'Font of the status label on the order tracking page.',
  ),
  subtitleColor: colorParam(
    'wst-heading-2-color',
    'color-title',
    'Text color of the order status heading on the order tracking page.',
  ),
  subtitleFont: fontParam(
    'Heading-M',
    'h2',
    24,
    'Font of the order status heading on the order tracking page.',
  ),
  bodyColor: colorParam(
    'wst-shade-3-color',
    'color-fill-base-shade-3',
    'Text color of the order status description on the order tracking page.',
  ),
  bodyFont: fontParam(
    'Body-S',
    'p',
    14,
    'Font of the order status description on the order tracking page.',
  ),
  sectionLabelColor: colorParam(
    'wst-heading-2-color',
    'color-title',
    'Text color of the fulfillment details heading on the order tracking page.',
  ),
  sectionLabelFont: fontParam(
    'Heading-M',
    'h3',
    16,
    'Font of the fulfillment details heading on the order tracking page.',
  ),
  detailLabelsColor: colorParam(
    'wst-paragraph-2-color',
    'color-text-primary',
    'Text color of the fulfillment detail labels on the order tracking page.',
  ),
  detailLabelsFont: fontParam(
    'Body-S',
    'p',
    14,
    'Font of the fulfillment detail labels on the order tracking page.',
  ),
  detailTextColor: colorParam(
    'wst-shade-3-color',
    'color-fill-base-shade-3',
    'Text color of the fulfillment detail values on the order tracking page.',
  ),
  detailTextFont: fontParam(
    'Body-M',
    'p',
    16,
    'Font of the fulfillment detail values on the order tracking page.',
  ),

  ...legacyCardStylesParams,

  fulfillmentBackgroundColor: colorParam(
    'wst-primary-background-color',
    'color-fill-background-primary',
    'Background fill color of the fulfillment details card.',
    StyleType.backgroundColor,
  ),
  fulfillmentBorderWidth: numberParam(
    StyleType.borderWidth,
    0,
    'Border thickness in pixels of the fulfillment details card.',
  ),
  fulfillmentBorderColor: colorParam(
    'wst-paragraph-2-color',
    'color-text-primary',
    'Border color of the fulfillment details card.',
    StyleType.borderColor,
    0.2,
  ),
  fulfillmentCornerRadiusTopLeft: numberParam(
    StyleType.borderTopLeftRadius,
    0,
    'Top-left corner radius in pixels of the fulfillment details card.',
  ),
  fulfillmentCornerRadiusTopRight: numberParam(
    StyleType.borderTopRightRadius,
    0,
    'Top-right corner radius in pixels of the fulfillment details card.',
  ),
  fulfillmentCornerRadiusBottomLeft: numberParam(
    StyleType.borderBottomLeftRadius,
    0,
    'Bottom-left corner radius in pixels of the fulfillment details card.',
  ),
  fulfillmentCornerRadiusBottomRight: numberParam(
    StyleType.borderBottomRightRadius,
    0,
    'Bottom-right corner radius in pixels of the fulfillment details card.',
  ),
  fulfillmentCornerRadiusLinked: numberParam(
    StyleType.numberEnum,
    0,
    'Controls whether the four corner radii of the fulfillment details card are edited together: 1 = linked, 0 = independent.',
  ),
  dividerWidth: numberParam(
    StyleType.borderWidth,
    1,
    'Thickness in pixels of the divider in the fulfillment details card.',
  ),
  dividerColor: colorParam(
    'wst-paragraph-2-color',
    'color-text-primary',
    'Color of the divider in the fulfillment details card.',
    StyleType.borderColor,
    0.2,
  ),

  appBackgroundColor: colorParam(
    'wst-primary-background-color',
    'color-fill-background-primary',
    'Background color of the overall order tracking page.',
    StyleType.backgroundColor,
  ),
});

export const headerStylesParams = {
  titleColor: designStylesParams.titleColor,
  titleFont: designStylesParams.titleFont,
  orderNumberColor: designStylesParams.orderNumberColor,
  orderNumberFont: designStylesParams.orderNumberFont,
};

export const orderStatusStylesParams = {
  statusColor: designStylesParams.statusColor,
  statusFont: designStylesParams.statusFont,
  subtitleColor: designStylesParams.subtitleColor,
  subtitleFont: designStylesParams.subtitleFont,
  bodyColor: designStylesParams.bodyColor,
  bodyFont: designStylesParams.bodyFont,
  backgroundColor: designStylesParams.backgroundColor,
  backgroundBorderWidth: designStylesParams.backgroundBorderWidth,
  backgroundBorderColor: designStylesParams.backgroundBorderColor,
  backgroundCornerRadiusTopLeft:
    designStylesParams.backgroundCornerRadiusTopLeft,
  backgroundCornerRadiusTopRight:
    designStylesParams.backgroundCornerRadiusTopRight,
  backgroundCornerRadiusBottomLeft:
    designStylesParams.backgroundCornerRadiusBottomLeft,
  backgroundCornerRadiusBottomRight:
    designStylesParams.backgroundCornerRadiusBottomRight,
  backgroundCornerRadiusLinked: designStylesParams.backgroundCornerRadiusLinked,
};

export const fulfillmentDetailsStylesParams = {
  sectionLabelColor: designStylesParams.sectionLabelColor,
  sectionLabelFont: designStylesParams.sectionLabelFont,
  detailLabelsColor: designStylesParams.detailLabelsColor,
  detailLabelsFont: designStylesParams.detailLabelsFont,
  detailTextColor: designStylesParams.detailTextColor,
  detailTextFont: designStylesParams.detailTextFont,
  fulfillmentBackgroundColor: designStylesParams.fulfillmentBackgroundColor,
  fulfillmentBorderWidth: designStylesParams.fulfillmentBorderWidth,
  fulfillmentBorderColor: designStylesParams.fulfillmentBorderColor,
  fulfillmentCornerRadiusTopLeft:
    designStylesParams.fulfillmentCornerRadiusTopLeft,
  fulfillmentCornerRadiusTopRight:
    designStylesParams.fulfillmentCornerRadiusTopRight,
  fulfillmentCornerRadiusBottomLeft:
    designStylesParams.fulfillmentCornerRadiusBottomLeft,
  fulfillmentCornerRadiusBottomRight:
    designStylesParams.fulfillmentCornerRadiusBottomRight,
  fulfillmentCornerRadiusLinked:
    designStylesParams.fulfillmentCornerRadiusLinked,
  dividerWidth: designStylesParams.dividerWidth,
  dividerColor: designStylesParams.dividerColor,
};

export const generalStylesParams = {
  appBackgroundColor: designStylesParams.appBackgroundColor,
};
